/**
 * Tipos del inventario operativo (libreta de campo).
 *
 * Fuente actual: Supabase (tablas parcelas + propietarios + gestiones, AES-256-GCM
 * en columnas de PII). Los valores fluyen via REST/RPC con RLS por tenant.
 *
 * NOTA SOBRE PII:
 *   - El campo `titular` (nombre completo) y `telefono` SON PII.
 *   - El cliente_elawan los ve enmascarados (`titular_masked`, `tiene_telefono`).
 *   - El tecnico_aosenuma los ve en claro bajo MFA reciente (RPC `unmask_propietario`).
 *   - Encriptación server-side: lib-crypto AES-256-GCM via Web Crypto.
 */

export type InteresConfirmado = 'si' | 'pd' | 'sin_tocar';

/**
 * Estado del semaforo · 5 fases del ciclo de gestión.
 *
 *   sin_prospectar (gris)  · parcela aún no contactada
 *   en_valoracion  (azul)  · contacto inicial, evaluando interés
 *   en_negociacion (verde) · negociación activa de términos
 *   contratada     (lime)  · firma cerrada, en operación
 *   inviable       (rojo)  · descartada por irregularidad o vacancia
 *
 * Reglas de transición y precedencia viven en `semaforo.ts`
 * (`estadoEfectivoFromGestion`).
 */
export const ESTADOS_SEMAFORO = [
  'sin_prospectar',
  'en_valoracion',
  'en_negociacion',
  'contratada',
  'inviable',
] as const;
export type EstadoSemaforo = (typeof ESTADOS_SEMAFORO)[number];

/** Etiqueta canonica en es-MX (con tildes) para mostrar en UI. */
export const LABEL_ESTADO_SEMAFORO: Record<EstadoSemaforo, string> = {
  sin_prospectar: 'Sin prospectar',
  en_valoracion:  'En valoración',
  en_negociacion: 'En negociación',
  contratada:     'Contratada',
  inviable:       'Inviable',
};

/**
 * Mapeo `interes` (3 valores legacy: si/pd/sin_tocar) → `EstadoSemaforo` (5).
 * Centralizado para que toda la app use la misma traducción.
 */
export function interesToEstadoSemaforo(i: InteresConfirmado): EstadoSemaforo {
  switch (i) {
    case 'si':         return 'en_negociacion';
    case 'pd':         return 'en_valoracion';
    case 'sin_tocar':  return 'sin_prospectar';
  }
}

/**
 * Inversa de `interesToEstadoSemaforo`. El semáforo tiene 5 valores y el
 * `interes` solo 3, así que la mapeamos colapsando:
 *   contratada / en_negociacion → 'si'  (hubo señal de interés)
 *   en_valoracion              → 'pd'   (pendiente de definir)
 *   sin_prospectar / inviable  → 'sin_tocar'
 */
export function interesFromEstadoSemaforo(s: EstadoSemaforo): InteresConfirmado {
  switch (s) {
    case 'contratada':
    case 'en_negociacion': return 'si';
    case 'en_valoracion':  return 'pd';
    case 'sin_prospectar':
    case 'inviable':       return 'sin_tocar';
  }
}

export type PoligonoCampo = 1 | 2 | 'PROPIEDAD PRIVADA';
export type TipoParcelaCampo = 'PARCELA_INDIVIDUAL' | 'VACANTE_NUCLEO_AGRARIO' | string;

export interface InventarioRow {
  poligono: PoligonoCampo | null;
  zona: number | null;
  num_parcela: string | null;            // mantener como string ("42A" admite sufijos)
  num_parcela_int: number | null;        // numero parseable para busqueda/orden
  tipo_parcela: TipoParcelaCampo | null;
  folio_tierras: string | null;
  folio_derechos: string | null;
  clave_catastral: string | null;
  area_ha: number | null;
  reunion_fecha: string | null;          // ISO YYYY-MM-DD
  dias_desde_reunion: number | null;     // calc al momento del build
  titular: string | null;                // PII (clear) — solo via BFF /v1/propietarios/full
  titular_masked: string | null;         // 'AGUILAR B. C.'
  telefono: string | null;               // PII (clear)
  tiene_telefono: boolean;
  /** UUID del propietario (fk a `propietarios.id`). Lo usa el reporte
   *  PDF para hacer match de titulares descifrados via BFF. */
  propietario_id: string | null;
  /**
   * ID público de parcela (`parcelas.parcela_id`, p. ej. HUM-P1127).
   * Enlaza la fila con el mapa y `v_parcelas_publicas` para KPIs alineados al vivo.
   */
  parcela_id?: string | null;
  /**
   * Última actualización de la gestión (`gestiones.updated_at`). Señal real
   * de actividad del técnico — usada para ordenar el ranking ejecutivo por
   * "más reciente primero" dentro de cada estado del semáforo. ISO 8601.
   * Null si la parcela aún no tiene fila en `gestiones`.
   */
  gestion_updated_at?: string | null;
  interes: InteresConfirmado;
  estado_semaforo: EstadoSemaforo;       // derivado: vacante -> 'inviable'
  /**
   * Avance del expediente digital (0–100). Opcional hasta integrar fuente formal
   * (Supabase / R2). `null` o ausente = sin dato en UI.
   */
  expediente_completitud_pct?: number | null;
}

export interface ParcelaPorTitular {
  num_parcela: string | null;
  num_parcela_int: number | null;
  poligono: PoligonoCampo | null;
  area_ha: number | null;
  interes: InteresConfirmado;
}

export interface TitularGroup {
  titular: string;
  titular_masked: string;
  tiene_telefono: boolean;
  parcelas: ParcelaPorTitular[];
  area_total_ha: number;
  interes_max: InteresConfirmado;
}

export interface InventarioAggregates {
  total: number;
  por_poligono: { P1: number; P2: number; PP: number };
  /** Distribucion por zona (`Z1`, `Z2`, ...). Casi siempre solo Z1. */
  por_zona: Record<string, number>;
  por_interes: { si: number; pd: number; sin_tocar: number };
  /** Distribucion del semaforo de 5 estados (PIPELINE secc. 8.1). */
  por_semaforo: {
    sin_prospectar: number;
    en_valoracion: number;
    en_negociacion: number;
    contratada: number;
    inviable: number;
  };
  con_reunion: number;
  sin_reunion: number;
  con_telefono: number;
  sin_telefono: number;
  area_total_ha: number;        // suma del Excel (todo el inventario operativo)
  area_si_ha: number;
  area_pd_ha: number;
  area_contratada_ha: number;
  area_inviable_ha: number;
}

export interface InventarioPendientes {
  llamar_pendientes: InventarioRow[];
  follow_up_pd: InventarioRow[];
  sin_telefono: InventarioRow[];
}

export interface InventarioFile {
  generado_at: string;
  fuente: string;
  fecha_corte: string;
  aggregates: InventarioAggregates;
  rows: InventarioRow[];
  titulares: TitularGroup[];
  pendientes: InventarioPendientes;
}
